import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import api from './api';

const ThemeContext = createContext({
  mode: 'light', effectiveMode: 'light',
  toggleMode: () => {}, setMode: () => {},
});

const STORAGE_KEY = 'app_theme_mode';
const VALID = ['light', 'dark', 'auto'];

// "Auto" = time-of-day aware. Light between sunrise and sunset (local time),
// dark otherwise. Users overwhelmingly keep their OS in dark-always, so
// prefers-color-scheme proved unreliable — time window matches expectation.
const AUTO_LIGHT_START = 6;   // 06:00 local → switch to light
const AUTO_LIGHT_END   = 18;  // 18:00 local → switch to dark

function isLightHour(d = new Date()) {
  const h = d.getHours();
  return h >= AUTO_LIGHT_START && h < AUTO_LIGHT_END;
}

function applyMode(effective) {
  const root = document.documentElement;
  if (effective === 'dark') root.classList.add('dark');
  else root.classList.remove('dark');
  root.setAttribute('data-theme', effective);
  try { root.style.colorScheme = effective; } catch (_) { console.debug('[caught]', _); /* noop */ }
}

export function ThemeProvider({ children }) {
  const [mode, setModeState] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return VALID.includes(saved) ? saved : 'auto';
    } catch (_) { return 'auto'; }
  });
  const [now, setNow] = useState(() => new Date());

  // Resolve "auto" using local time-of-day (sunrise/sunset window)
  const effectiveMode = useMemo(() => {
    if (mode === 'auto') return isLightHour(now) ? 'light' : 'dark';
    return mode;
  }, [mode, now]);

  useEffect(() => { applyMode(effectiveMode); }, [effectiveMode]);

  // Re-evaluate at the next boundary minute. We check every 60s — cheap and
  // always-correct within one minute of the flip. Re-checks anchor to real
  // Date() so device timezone changes (rare) are caught too.
  useEffect(() => {
    if (mode !== 'auto') return;
    const t = setInterval(() => setNow(new Date()), 60_000);
    // Also refresh immediately when the tab returns to foreground (phones
    // sleeping overnight won't fire the interval).
    const onVis = () => { if (!document.hidden) setNow(new Date()); };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      clearInterval(t);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [mode]);

  // On login pull preference from server (overrides local)
  useEffect(() => {
    const token = (() => {
      try { return localStorage.getItem('token'); } catch (_) { return null; }
    })();
    if (!token) return;
    let alive = true;
    api.get('/profile').then(r => {
      if (!alive) return;
      const srv = r.data?.theme_mode;
      if (VALID.includes(srv)) {
        setModeState(srv);
        try { localStorage.setItem(STORAGE_KEY, srv); } catch (_) { console.debug('[caught]', _); /* noop */ }
      }
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  const setMode = useCallback((next) => {
    if (!VALID.includes(next)) return;
    setModeState(next);
    try { localStorage.setItem(STORAGE_KEY, next); } catch (_) { console.debug('[caught]', _); /* noop */ }
    api.put('/profile/theme', { theme_mode: next }).catch(() => {});
  }, []);

  // Header toggle cycles between the two explicit modes (not auto).
  const toggleMode = useCallback(() => {
    setMode(effectiveMode === 'dark' ? 'light' : 'dark');
  }, [effectiveMode, setMode]);

  // Memoise the value so theme consumers don't re-render on the 60s `now`
  // tick (which only advances the clock and rarely flips effectiveMode).
  const value = useMemo(
    () => ({ mode, effectiveMode, toggleMode, setMode }),
    [mode, effectiveMode, toggleMode, setMode]
  );

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}
